import { useCallback } from 'react'

import { zodResolver } from '@hookform/resolvers/zod'
import { Controller, FormProvider, useForm } from 'react-hook-form'

import { FormController } from '@/components/ui/FormController'
import { Button } from '@/components/ui/button'
import { Dialog } from '@/components/ui/dialog'
import { TextField } from '@/components/ui/text-field'
import type { NoSpendersListFormValues } from '@/modules/creator-settings/lists/no-spenders/types'
import { NoSpendersListFormSchema } from '@/modules/creator-settings/lists/no-spenders/types'

import type { DialogProps } from '../types'

interface Props extends DialogProps {
  onSubmit: (values: NoSpendersListFormValues) => Promise<unknown | undefined>
  title: string
  initialValues?: NoSpendersListFormValues
  submitLabel?: string
  isLoading?: boolean
}

export const NoSpenderListsFormDialog = ({
  isOpen,
  onClose,
  onSubmit,
  title,
  initialValues,
  submitLabel = 'Save',
  isLoading = false,
}: Props) => {
  const form = useForm<NoSpendersListFormValues>({
    resolver: zodResolver(NoSpendersListFormSchema),
    defaultValues: {
      name: initialValues?.name ?? '',
      minimumDays: initialValues?.minimumDays ?? 1,
    },
    mode: 'onChange',
  })

  const { handleSubmit } = form

  const handleFormSubmit = useCallback(
    async (values: NoSpendersListFormValues) => {
      await onSubmit(values)

      onClose()
    },
    [onClose, onSubmit],
  )

  return (
    <Dialog
      show={isOpen}
      onClose={onClose}
      panelClassName="w-[400px]"
      testId="no-spender-list-modal"
    >
      <FormProvider {...form}>
        <form onSubmit={handleSubmit(handleFormSubmit)}>
          <Dialog.Header>
            <Dialog.Title>{title}</Dialog.Title>
          </Dialog.Header>
          <Dialog.Description>
            <div className="flex flex-col gap-6 pt-6">
              <Controller
                name="name"
                render={({ field, fieldState }) => (
                  <FormController>
                    <FormController.Label htmlFor="list-name">
                      List Name
                    </FormController.Label>
                    <FormController.Input>
                      <TextField
                        {...field}
                        onChange={field.onChange}
                        onBlur={() => {
                          field.onBlur()
                          field.onChange(field.value.trim())
                        }}
                        id="list-name"
                        placeholder="Enter list name"
                        testId="list-name"
                      />
                    </FormController.Input>
                    {fieldState.error?.message && (
                      <FormController.Error>
                        {fieldState.error?.message}
                      </FormController.Error>
                    )}
                  </FormController>
                )}
              />
              <Controller
                name="minimumDays"
                render={({ field, fieldState }) => (
                  <FormController>
                    <FormController.Label htmlFor="minimum-days">
                      Minimum
                    </FormController.Label>
                    <FormController.Input>
                      <TextField
                        {...field}
                        onChange={field.onChange}
                        id="minimum-days"
                        type="number"
                        placeholder="Enter minimum days"
                        suffix="Days"
                        showSuffixSeparator
                        testId="minimum-days"
                      />
                    </FormController.Input>
                    {!fieldState.error?.message && (
                      <FormController.Helper>
                        Minimum 1 day for custom no spenders list
                      </FormController.Helper>
                    )}
                    {fieldState.error?.message && (
                      <FormController.Error>
                        {fieldState.error?.message}
                      </FormController.Error>
                    )}
                  </FormController>
                )}
              />
            </div>
          </Dialog.Description>
          <Dialog.Footer>
            <Button
              type="button"
              onClick={onClose}
              className="flex-1"
              variant="outline"
            >
              Cancel
            </Button>
            <Button
              isLoading={isLoading || form.formState.isSubmitting}
              className="flex-1"
              testId="no-spender-list-submit-button"
            >
              {submitLabel}
            </Button>
          </Dialog.Footer>
        </form>
      </FormProvider>
    </Dialog>
  )
}
